import React, { useState } from 'react';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';

interface ChatSessionHeaderProps {
  sessionTitle: string;
  onRename: (newTitle: string) => void;
}

const ChatSessionHeader: React.FC<ChatSessionHeaderProps> = ({ sessionTitle, onRename }) => {
  const { isDarkMode } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(sessionTitle);

  const handleStartEdit = () => {
    setEditValue(sessionTitle);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editValue.trim() && editValue !== sessionTitle) {
      onRename(editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(sessionTitle);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className={`session-header ${isDarkMode ? 'dark-theme' : 'light-theme'}`}>
      <div className="header-content">
        {/* Custom Icon - Cyan gradient */}
        <div className="header-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#06B6D4" />
                <stop offset="100%" stopColor="#3B82F6" />
              </linearGradient>
            </defs>
            <path d="M8 10H16M8 14H16M6 22H18C19.1046 22 20 21.1046 20 20V4C20 2.89543 19.1046 2 18 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22Z"
              stroke="url(#iconGradient)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="6" r="1" fill="#06B6D4"/>
          </svg>
        </div>

        {isEditing ? (
          <div className="edit-container">
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              className="title-input"
            />
            <button onClick={handleSave} className="action-button save">
              <CheckIcon />
            </button>
            <button onClick={handleCancel} className="action-button cancel">
              <XMarkIcon />
            </button>
          </div>
        ) : (
          <div className="title-container">
            <h1 className="session-title">{sessionTitle}</h1>
            <button onClick={handleStartEdit} className="edit-button">
              <PencilIcon />
            </button>
          </div>
        )}
      </div>

      <style>{`
        .session-header {
          border-bottom: 1px solid rgba(100, 116, 139, 0.2);
          background: rgba(15, 23, 42, 0.8);
          backdrop-filter: blur(12px);
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .session-header.light-theme {
          border-bottom: 1px solid rgba(209, 213, 219, 0.5);
          background: rgba(255, 255, 255, 0.9);
        }

        .header-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 16px 24px;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        /* Header Icon - Cyan gradient */
        .header-icon {
          width: 32px;
          height: 32px;
          flex-shrink: 0;
        }

        .header-icon svg {
          width: 100%;
          height: 100%;
        }

        /* Title Container */
        .title-container {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .session-title {
          font-size: 18px;
          font-weight: 600;
          color: #F8FAFC;
          margin: 0;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .light-theme .session-title {
          color: #1F2937;
        }

        .edit-button {
          opacity: 0;
          padding: 6px;
          background: rgba(100, 116, 139, 0.2);
          border: none;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .light-theme .edit-button {
          background: rgba(107, 114, 128, 0.1);
        }

        .session-header:hover .edit-button {
          opacity: 1;
        }

        .edit-button:hover {
          background: rgba(100, 116, 139, 0.3);
        }

        .light-theme .edit-button:hover {
          background: rgba(6, 182, 212, 0.1);
        }

        .edit-button svg {
          width: 16px;
          height: 16px;
          color: #94A3B8;
        }

        .light-theme .edit-button svg {
          color: #6B7280;
        }

        /* Edit Container */
        .edit-container {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .title-input {
          flex: 1;
          padding: 6px 12px;
          font-size: 18px;
          font-weight: 600;
          color: #F8FAFC;
          background: rgba(100, 116, 139, 0.15);
          border: 2px solid #06B6D4;
          border-radius: 8px;
          outline: none;
        }

        .light-theme .title-input {
          color: #1F2937;
          background: rgba(255, 255, 255, 1);
          border: 2px solid #06B6D4;
        }

        .title-input:focus {
          box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.2);
        }

        .light-theme .title-input:focus {
          box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.15);
        }

        .action-button {
          padding: 6px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .action-button svg {
          width: 18px;
          height: 18px;
        }

        .action-button.save {
          background: #10b981;
          color: white;
        }

        .action-button.save:hover {
          background: #059669;
        }

        .action-button.cancel {
          background: rgba(100, 116, 139, 0.2);
          color: #94A3B8;
        }

        .light-theme .action-button.cancel {
          background: rgba(107, 114, 128, 0.1);
          color: #6B7280;
        }

        .action-button.cancel:hover {
          background: rgba(100, 116, 139, 0.3);
        }

        .light-theme .action-button.cancel:hover {
          background: rgba(107, 114, 128, 0.15);
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .header-content {
            padding: 12px 16px;
          }

          .header-icon {
            width: 28px;
            height: 28px;
          }

          .session-title {
            font-size: 16px;
          }

          .title-input {
            font-size: 16px;
            padding: 4px 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default ChatSessionHeader;
